import { useState } from "react";
import { Calendar, Button } from "antd";
import type { CalendarProps } from "antd/es/calendar";
import type { CalendarMode } from "antd/es/calendar/generateCalendar";
import {
  faCalendarDay,
  faCalendarWeek,
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs, { Dayjs } from "dayjs";
import { BaseFilterProps } from "interfaces/filter-component-base";
import "./DateRangeFilter.scss";

type PresetType = "today" | "last7days" | "last30days" | "custom";

const DateRangeFilter = (props: BaseFilterProps) => {
  const { onChangeValues, values } = props;
  const [showCustomRange, setShowCustomRange] = useState(
    values?.preset === "custom"
  );
  const [selectedPreset, setSelectedPreset] = useState<PresetType | null>(
    values?.preset
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(
    values?.startDate ? dayjs(values?.startDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    values?.endDate ? dayjs(values?.endDate) : null
  );

  const handlePresetClick = (preset: PresetType) => {
    setSelectedPreset(preset);
    setShowCustomRange(false);

    let startDate: Dayjs;
    let endDate: Dayjs = dayjs().endOf("day");

    switch (preset) {
      case "today":
        startDate = dayjs().startOf("day");
        break;
      case "last7days":
        startDate = dayjs().subtract(6, "days").startOf("day");
        break;
      case "last30days":
        startDate = dayjs().subtract(29, "days").startOf("day");
        break;
      default:
        return;
    }

    if (onChangeValues) {
      onChangeValues({
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  const handleCustomRangeClick = () => {
    setShowCustomRange(true);
    setSelectedPreset("custom");
  };

  const handleBackToPresets = () => {
    setShowCustomRange(false);
  };

  const handleCalendarSelect = (date: Dayjs, event: any) => {
    if (event.source !== "date") return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (startDate && !endDate) {
      if (date.isBefore(startDate)) {
        const newEndDate = startDate;
        setStartDate(date);
        setEndDate(newEndDate);
        updateDateRange([date, newEndDate]);
      } else {
        setEndDate(date);
        updateDateRange([startDate, date]);
      }
    }
  };

  const updateDateRange = (range: [Dayjs, Dayjs]) => {
    const startDateStr = range[0].format("YYYY-MM-DD");
    const endDateStr = range[1].format("YYYY-MM-DD");

    if (onChangeValues) {
      onChangeValues({
        preset: "custom",
        startDate: startDateStr,
        endDate: endDateStr,
      });
    }
  };

  const getDateCellClass = (date: Dayjs): string => {
    if (!startDate) return "";

    const dateStr = date.format("YYYY-MM-DD");
    const startStr = startDate.format("YYYY-MM-DD");

    if (endDate) {
      const endStr = endDate.format("YYYY-MM-DD");
      if (dateStr === startStr) {
        return "range-start";
      }
      if (dateStr === endStr) {
        return "range-end";
      }
      if (date.isAfter(startDate) && date.isBefore(endDate)) {
        return "range-middle";
      }
    } else {
      if (dateStr === startStr) {
        return "range-start";
      }
    }

    return "";
  };

  const dateCellRender: CalendarProps<Dayjs>["dateCellRender"] = (
    date: Dayjs
  ) => {
    const cellClass = getDateCellClass(date);
    return <div className={`calendar-cell ${cellClass}`}></div>;
  };

  if (showCustomRange) {
    return (
      <div className="date-range-filter">
        <div className="date-range-header">
          <Button
            type="link"
            onClick={handleBackToPresets}
            className="back-to-presets"
          >
            &lt; Back to Presets
          </Button>
          <h3 className="custom-range-title">Custom Range</h3>
        </div>
        <div className="custom-range-calendar">
          <Calendar
            fullscreen={false}
            onSelect={handleCalendarSelect}
            cellRender={dateCellRender}
            headerRender={({
              value,
              onChange,
            }: {
              value: Dayjs;
              type: CalendarMode;
              onChange: (date: Dayjs) => void;
              onTypeChange: (type: CalendarMode) => void;
            }) => {
              const month = value.format("MMMM YYYY");
              return (
                <div className="calendar-header">
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faChevronLeft} />}
                    onClick={() => {
                      const newValue = value
                        .subtract(1, "month")
                        .startOf("month");
                      onChange(newValue);
                    }}
                    className="calendar-nav-button"
                  />
                  <span className="calendar-month-year">{month}</span>
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faChevronRight} />}
                    onClick={() => {
                      const newValue = value.add(1, "month").startOf("month");
                      onChange(newValue);
                    }}
                    className="calendar-nav-button"
                  />
                </div>
              );
            }}
            mode="month"
          />
        </div>
        <div className="date-inputs">
          <div className="date-input start-date">
            <FontAwesomeIcon
              icon={faCalendarDay}
              className="date-input-icon blue"
            />
            <div className="date-input-content">
              <span className="date-input-label">Start Date</span>
            </div>
            <div>
              <span className="date-input-value">
                {startDate ? startDate.format("MMM D, YYYY") : "Not selected"}
              </span>
            </div>
          </div>
          <div className="date-input end-date">
            <FontAwesomeIcon
              icon={faCalendarDay}
              className="date-input-icon purple"
            />
            <div className="date-input-content">
              <span className="date-input-label">End Date</span>
            </div>
            <div>
              <span className="date-input-value">
                {endDate ? endDate.format("MMM D, YYYY") : "Not selected"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="date-range-filter">
      <h3 className="presets-title">QUICK PRESETS</h3>
      <div className="date-range-presets">
        <div
          className={`preset-item ${
            selectedPreset === "today" ? "selected" : ""
          }`}
          onClick={() => handlePresetClick("today")}
        >
          <div className="preset-icon blue">
            <FontAwesomeIcon icon={faCalendarDay} />
          </div>
          <div className="preset-content">
            <p className="preset-title">Today</p>
            <p className="preset-description">Invites sent today</p>
          </div>
        </div>
        <div
          className={`preset-item ${
            selectedPreset === "last7days" ? "selected" : ""
          }`}
          onClick={() => handlePresetClick("last7days")}
        >
          <div className="preset-icon purple">
            <FontAwesomeIcon icon={faCalendarWeek} />
          </div>
          <div className="preset-content">
            <p className="preset-title">Last 7 Days</p>
            <p className="preset-description">Past week</p>
          </div>
        </div>
        <div
          className={`preset-item ${
            selectedPreset === "last30days" ? "selected" : ""
          }`}
          onClick={() => handlePresetClick("last30days")}
        >
          <div className="preset-icon green">
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <div className="preset-content">
            <p className="preset-title">Last 30 Days</p>
            <p className="preset-description">Past month</p>
          </div>
        </div>
        <div
          className={`preset-item custom-range ${
            selectedPreset === "custom" ? "selected" : ""
          }`}
          onClick={handleCustomRangeClick}
        >
          <div className="preset-icon orange"></div>
          <div className="preset-content">
            <p className="preset-title">Custom Range</p>
            <p className="preset-description">Pick specific dates</p>
          </div>
          <div className="preset-arrow">
            <span>&gt;</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilter;
